import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  buildEntityInputValue,
  CANONICAL_TOPICS,
  formatEntityLabel,
  normalizeEntityInput,
} from '@/lib/tracking';
import { invalidateFeed } from '@/lib/feed-sync';
import {
  clearSupabaseResourceMissing,
  isMissingSupabaseResource,
  isSupabaseResourceMarkedMissing,
  markSupabaseResourceMissing,
} from '@/lib/supabase-errors';

interface TopicPreference {
  topic: string;
}

interface EntityRecord {
  id: string;
  canonical_key: string;
  name: string;
  kind: string;
  ticker: string | null;
}

interface EntitySuggestion extends EntityRecord {
  matched_alias: string | null;
  match_score: number;
}

interface EntityTrackingRow {
  entity_id: string;
}

interface TrendSnapshot {
  id: string;
  label: string;
  summary: string;
  confidence: number;
  topics: string[];
  entity_ids: string[];
}

const CANONICAL_TOPIC_SET = new Set<string>(CANONICAL_TOPICS);

export function TrackingPage() {
  const { supabaseClient } = useAuth();
  const [userId, setUserId] = useState<string | null>(null);
  const [topics, setTopics] = useState<TopicPreference[]>([]);
  const [entities, setEntities] = useState<EntityRecord[]>([]);
  const [, setTrendSuggestions] = useState<TrendSnapshot[]>([]);
  const [, setSuggestedEntities] = useState<EntityRecord[]>([]);
  const [entitySuggestions, setEntitySuggestions] = useState<EntitySuggestion[]>([]);
  const [entitySearchUnavailable, setEntitySearchUnavailable] = useState(() =>
    isSupabaseResourceMarkedMissing('search_entities', null)
  );
  const [entitySearchDegraded, setEntitySearchDegraded] = useState(false);
  const [trendSignalsUnavailable, setTrendSignalsUnavailable] = useState(() =>
    isSupabaseResourceMarkedMissing('trend_snapshots', null)
  );
  const [, setTrendSignalsLive] = useState(true);
  const [searchingEntities, setSearchingEntities] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve Supabase user ID from the active session
  useEffect(() => {
    if (!supabaseClient) {
      setUserId(null);
      return;
    }

    // Get current session
    void supabaseClient.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    // Subscribe to auth changes (catches async Edge Function session)
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, [supabaseClient]);

  useEffect(() => {
    setEntitySearchUnavailable(isSupabaseResourceMarkedMissing('search_entities', userId));
    setEntitySearchDegraded(false);
    setTrendSignalsUnavailable(isSupabaseResourceMarkedMissing('trend_snapshots', userId));
    setTrendSignalsLive(true);
  }, [userId]);

  const loadTracking = useCallback(async () => {
    if (!userId || !supabaseClient) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const trendResourceMissing = isSupabaseResourceMarkedMissing('trend_snapshots', userId);

    if (trendResourceMissing) {
      setTrendSignalsUnavailable(true);
      setTrendSignalsLive(false);
    }

    const [topicsRes, entityLinksRes, trendsRes] = await Promise.all([
      supabaseClient
        .from('user_topic_preferences')
        .select('topic')
        .eq('user_id', userId)
        .eq('muted', false)
        .order('topic'),
      supabaseClient
        .from('user_entity_tracking')
        .select('entity_id')
        .eq('user_id', userId)
        .eq('muted', false),
      trendSignalsUnavailable || trendResourceMissing
        ? Promise.resolve({ data: [], error: null })
        : supabaseClient
            .from('trend_snapshots')
            .select('id, label, summary, confidence, topics, entity_ids')
            .order('confidence', { ascending: false })
            .limit(8),
    ]);

    if (topicsRes.error || entityLinksRes.error) {
      setError('Could not load your tracking preferences right now.');
      setLoading(false);
      return;
    }

    const entityIds = (entityLinksRes.data ?? []).map((row: EntityTrackingRow) => row.entity_id);
    let entityRows: EntityRecord[] = [];
    const activeTopics = ((topicsRes.data ?? []) as TopicPreference[]).filter((entry) =>
      CANONICAL_TOPIC_SET.has(entry.topic)
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const trendRows = (trendsRes.data ?? []).map((row: any) => ({
      id: row.id,
      label: row.label,
      summary: row.summary,
      confidence: row.confidence ?? 0,
      topics: Array.isArray(row.topics) ? row.topics : [],
      entity_ids: Array.isArray(row.entity_ids) ? row.entity_ids : [],
    })) as TrendSnapshot[];

    if (trendsRes.error) {
      const missingTrendResource = isMissingSupabaseResource(trendsRes.error);
      setTrendSignalsUnavailable(missingTrendResource);
      if (missingTrendResource) {
        markSupabaseResourceMissing('trend_snapshots', userId);
      }
      setTrendSignalsLive(false);
    } else {
      clearSupabaseResourceMissing('trend_snapshots', userId);
      setTrendSignalsUnavailable(false);
      setTrendSignalsLive(true);
    }

    if (entityIds.length > 0) {
      const { data: entitiesRes, error: entitiesError } = await supabaseClient
        .from('entities')
        .select('id, canonical_key, name, kind, ticker')
        .in('id', entityIds)
        .order('name');

      if (entitiesError) {
        setError('Could not load your tracked entities right now.');
        setLoading(false);
        return;
      }

      entityRows = entitiesRes ?? [];
    }

    let suggestedEntityRows: EntityRecord[] = [];
    const candidateEntityIds = [
      ...new Set(
        trendRows
          .flatMap((trend) => trend.entity_ids)
          .filter((entityId) => !entityRows.some((entity) => entity.id === entityId))
      ),
    ];

    if (candidateEntityIds.length > 0 && entityRows.length < 5) {
      const { data: suggestedEntitiesRes, error: suggestedEntitiesError } = await supabaseClient
        .from('entities')
        .select('id, canonical_key, name, kind, ticker')
        .in('id', candidateEntityIds.slice(0, 8))
        .order('name');

      if (!suggestedEntitiesError) {
        suggestedEntityRows = suggestedEntitiesRes ?? [];
      }
    }

    setTopics(activeTopics);
    setEntities(entityRows);
    setTrendSuggestions(trendRows);
    setSuggestedEntities(suggestedEntityRows.slice(0, 6));
    setLoading(false);
  }, [userId, supabaseClient, trendSignalsUnavailable]);

  useEffect(() => {
    if (!userId || !supabaseClient) return;
    void loadTracking();
  }, [userId, supabaseClient, loadTracking]);

  useEffect(() => {
    if (!userId || !supabaseClient) return;

    const query = input.trim();
    const entitySearchResourceMissing = isSupabaseResourceMarkedMissing('search_entities', userId);
    if (query.length < 2 || entitySearchUnavailable || entitySearchResourceMissing) {
      if (entitySearchResourceMissing && !entitySearchUnavailable) {
        setEntitySearchUnavailable(true);
        setEntitySearchDegraded(true);
      }
      setEntitySuggestions([]);
      setSearchingEntities(false);
      return;
    }

    const timeoutId = window.setTimeout(
      () =>
        void (async () => {
          setSearchingEntities(true);

          const { data, error: searchError } = await supabaseClient.rpc('search_entities', {
            p_query: query,
            p_limit: 6,
          });

          if (searchError) {
            setEntitySuggestions([]);
            if (isMissingSupabaseResource(searchError)) {
              markSupabaseResourceMissing('search_entities', userId);
              setEntitySearchUnavailable(true);
            }
            setEntitySearchDegraded(true);
            setSearchingEntities(false);
            return;
          }

          clearSupabaseResourceMissing('search_entities', userId);
          setEntitySearchDegraded(false);
          setEntitySearchUnavailable(false);
          setEntitySuggestions(
            ((data ?? []) as EntitySuggestion[]).filter(
              (entity) => !entities.some((tracked) => tracked.id === entity.id)
            )
          );
          setSearchingEntities(false);
        })(),
      180
    );

    return () => window.clearTimeout(timeoutId);
  }, [userId, supabaseClient, input, entities, entitySearchUnavailable]);

  function isTopicActive(topic: string): boolean {
    return topics.some((entry) => entry.topic === topic);
  }

  async function toggleTopic(topic: string) {
    if (!userId || !supabaseClient) return;
    if (!CANONICAL_TOPIC_SET.has(topic)) {
      setError('That topic is not available right now.');
      return;
    }
    setError(null);

    const active = isTopicActive(topic);
    if (active) {
      const { error: deleteError } = await supabaseClient
        .from('user_topic_preferences')
        .delete()
        .eq('user_id', userId)
        .eq('topic', topic);

      if (deleteError) {
        setError('Could not update that topic right now.');
        return;
      }

      setTopics((prev) => prev.filter((entry) => entry.topic !== topic));
      invalidateFeed(`topic-removed:${topic}`);
      return;
    }

    const { error: insertError } = await supabaseClient.from('user_topic_preferences').upsert(
      {
        user_id: userId,
        topic,
        weight: 1,
        muted: false,
      },
      { onConflict: 'user_id,topic' }
    );

    if (insertError) {
      setError('Could not update that topic right now.');
      return;
    }

    setTopics((prev) => [...prev, { topic }].sort((a, b) => a.topic.localeCompare(b.topic)));
    invalidateFeed(`topic-added:${topic}`);
  }

  async function trackEntityValue(rawValue: string, fallbackInvalidateKey?: string) {
    if (!userId || !supabaseClient) return false;

    const normalized = normalizeEntityInput(rawValue);
    if (!normalized) {
      setError('Enter a company name, @company, or stock ticker like NVDA.');
      return false;
    }

    if (entities.some((entity) => entity.canonical_key === normalized.canonicalKey)) {
      setInput('');
      setEntitySuggestions([]);
      return true;
    }

    setError(null);

    const { data, error: trackError } = await supabaseClient.rpc('track_entity', {
      p_input: rawValue.trim(),
    });

    if (trackError || !data || (Array.isArray(data) && data.length === 0)) {
      setError('Could not start tracking that entity.');
      return false;
    }

    setInput('');
    setEntitySuggestions([]);
    void loadTracking();
    invalidateFeed(fallbackInvalidateKey ?? `entity-added:${normalized.canonicalKey}`);
    return true;
  }

  async function addEntity() {
    await trackEntityValue(input);
  }

  async function removeEntity(entityId: string) {
    if (!userId || !supabaseClient) return;
    setError(null);

    const { error: deleteError } = await supabaseClient
      .from('user_entity_tracking')
      .delete()
      .eq('user_id', userId)
      .eq('entity_id', entityId);

    if (deleteError) {
      setError('Could not remove that entity right now.');
      return;
    }

    setEntities((prev) => prev.filter((entity) => entity.id !== entityId));
    invalidateFeed(`entity-removed:${entityId}`);
  }

  async function addSuggestedEntity(entity: EntityRecord) {
    await trackEntityValue(buildEntityInputValue(entity), `entity-added:${entity.canonical_key}`);
  }

  if (!supabaseClient) {
    return (
      <div className="page-shell">
        <div className="empty-state-panel">
          <p className="type-title" style={{ color: 'var(--text-primary)' }}>
            Tracking Unavailable
          </p>
          <p className="type-body" style={{ color: 'var(--text-secondary)' }}>
            Topic and entity tracking requires additional setup. Please sign out and sign back in.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="page-shell-narrow py-12 text-center"
        style={{ color: 'var(--text-tertiary)' }}
      >
        Loading tracking...
      </div>
    );
  }

  return (
    <div className="page-shell-narrow">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">What to follow</div>
          <h1 className="page-title">Tracking</h1>
          <p className="page-subtitle">
            Follow the themes and companies you care about so the feed stays focused.
          </p>
        </div>
        <div className="page-metrics">
          <span className="metric-chip type-caption">
            <strong>{topics.length}</strong>
            active topics
          </span>
          <span className="metric-chip type-caption">
            <strong>{entities.length}</strong>
            tracked entities
          </span>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 rounded-md px-3 py-2 type-caption"
          style={{
            background: 'var(--error-bg)',
            color: 'var(--error)',
            border: '1px solid rgba(224, 62, 62, 0.2)',
          }}
        >
          {error}
        </div>
      )}

      <div className="surface-panel mb-4">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="page-eyebrow">Topics</div>
            <div className="type-title-lg mt-1" style={{ color: 'var(--text-primary)' }}>
              Broad themes
            </div>
            <p
              className="type-caption mt-1"
              style={{ color: 'var(--text-tertiary)', maxWidth: '36rem' }}
            >
              Pick the broad areas you want to keep up with.
            </p>
          </div>
          <span className="metric-chip type-caption">
            <strong>{CANONICAL_TOPICS.length}</strong>
            topics
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {CANONICAL_TOPICS.map((topic) => {
            const active = isTopicActive(topic);
            return (
              <button
                key={topic}
                onClick={() => void toggleTopic(topic)}
                className="type-label rounded-full"
                style={{
                  padding: '7px 14px',
                  border: active
                    ? '1px solid var(--accent-primary)'
                    : '1px solid var(--border-primary)',
                  background: active ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.55)',
                  color: active ? '#ffffff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                  boxShadow: active ? '0 6px 18px rgba(35, 131, 226, 0.18)' : 'none',
                }}
              >
                {topic}
              </button>
            );
          })}
        </div>
      </div>

      <div className="surface-panel mb-4">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="page-eyebrow">Entities</div>
            <div className="type-title-lg mt-1" style={{ color: 'var(--text-primary)' }}>
              Track companies and tickers
            </div>
            <p
              className="type-caption mt-1"
              style={{ color: 'var(--text-tertiary)', maxWidth: '36rem' }}
            >
              {!entitySearchUnavailable && !entitySearchDegraded
                ? 'Use direct tracking for portfolio names, diligence targets, or public comps. Type plain text and the app will normalize it into a company or ticker record.'
                : entitySearchUnavailable
                  ? 'Use direct tracking for portfolio names, diligence targets, or public comps. Type plain text and the app will still normalize it while suggestions finish syncing in this environment.'
                  : 'Use direct tracking for portfolio names, diligence targets, or public comps. Type plain text and the app will still normalize it while suggestions are temporarily unavailable.'}
            </p>
          </div>
          <span className="metric-chip type-caption">
            <strong>Auto</strong>
            cleanup
          </span>
        </div>
        <div className="flex gap-2 flex-col sm:flex-row">
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void addEntity()}
            placeholder="e.g. Anthropic, NVDA, OpenAI"
            className="notion-input flex-1"
            style={{ fontSize: '14px' }}
          />
          <button
            onClick={() => void addEntity()}
            className="notion-btn notion-btn-primary type-label"
            style={{ padding: '8px 16px' }}
          >
            Add entity
          </button>
        </div>
        {(entitySearchUnavailable || entitySearchDegraded) && (
          <div className="type-caption mt-2" style={{ color: 'var(--text-tertiary)' }}>
            {entitySearchUnavailable
              ? 'Suggestions are still syncing in this environment. Manual add still works normally.'
              : 'Suggestions are temporarily unavailable. Manual add still works normally.'}
          </div>
        )}
        {(searchingEntities || entitySuggestions.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {searchingEntities && entitySuggestions.length === 0 && (
              <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
                Looking for matches...
              </span>
            )}
            {entitySuggestions.map((entity) => (
              <button
                key={entity.id}
                onClick={() => void addSuggestedEntity(entity)}
                className="pill-meta type-caption transition-notion"
                style={{
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                }}
                title={entity.matched_alias ? `Matched via ${entity.matched_alias}` : undefined}
              >
                + {formatEntityLabel(entity)}
                {entity.matched_alias &&
                  entity.matched_alias.toLowerCase() !== entity.name.toLowerCase() && (
                    <span style={{ color: 'var(--text-tertiary)' }}>
                      via {entity.matched_alias}
                    </span>
                  )}
              </button>
            ))}
          </div>
        )}
        <div className="type-caption mt-3" style={{ color: 'var(--text-tertiary)' }}>
          Good defaults: Anthropic, OpenAI, Databricks, NVDA, AMD.
        </div>
      </div>

      <div className="surface-panel">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="page-eyebrow">Tracked entities</div>
            <div className="type-title-lg mt-1" style={{ color: 'var(--text-primary)' }}>
              Companies you follow
            </div>
            <p
              className="type-caption mt-1"
              style={{ color: 'var(--text-tertiary)', maxWidth: '36rem' }}
            >
              Keep your main companies, comps, and watchlist names here.
            </p>
          </div>
        </div>

        {entities.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {entities.map((entity) => (
              <span
                key={entity.id}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full type-label"
                style={{
                  background: 'rgba(255, 255, 255, 0.55)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-primary)',
                }}
              >
                {formatEntityLabel(entity)}
                <span
                  className="pill-meta type-caption"
                  style={{
                    color:
                      entity.kind === 'ticker' ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                  }}
                >
                  {entity.kind}
                </span>
                <button
                  onClick={() => void removeEntity(entity.id)}
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: '24px',
                    height: '24px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-tertiary)',
                    fontSize: '14px',
                    lineHeight: 1,
                  }}
                  title="Remove"
                >
                  x
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="empty-state-panel" style={{ padding: '1.8rem 1.4rem' }}>
            <div className="page-eyebrow">No direct entities yet</div>
            <div className="type-title-lg" style={{ color: 'var(--text-primary)' }}>
              Start with companies you actively care about
            </div>
            <p className="page-subtitle" style={{ textAlign: 'center', maxWidth: '28rem' }}>
              Add a few names and the app will separate entity-specific coverage from broad topic
              noise.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
